import { useEffect, useRef } from 'react';
import { Alert, Button, Spin } from 'antd';
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import { useNavigate } from 'react-router';
import { incidencePaths, usersPaths } from 'constants/routePaths';
import {
  IMPORT_REJECTION_MESSAGE,
  IMPORT_SUCCESS_MESSAGE,
} from 'types/contractImport';
import { useContractImport } from './useContractImport';
import { ImportForm } from './ImportForm';
import { ImportFindings } from './ImportFindings';
import { ImportContextCard } from './ImportContextCard';
import { ImportHelp } from './ImportHelp';
import styles from './UploadContracts.module.scss';

const metrics = [
  ['contractsCreated', 'Contratos creados'],
  ['contractsReused', 'Contratos existentes usados'],
  ['schedulesCreated', 'Horarios agregados'],
  ['employeesProcessed', 'Empleados incluidos'],
] as const;
const number = new Intl.NumberFormat('es-MX');

export const UploadContracts = () => {
  const flow = useContractImport();
  const { state, showPrevious } = flow;
  const navigate = useNavigate();
  const heading = useRef<HTMLHeadingElement>(null);
  const rejection =
    state.kind === 'rejected'
      ? state.result
      : showPrevious
        ? flow.previous
        : undefined;
  const rows = rejection
    ? new Set(
        rejection.findings.flatMap((finding) =>
          finding.row === null ? [] : [finding.row]
        )
      ).size
    : 0;
  const errorCount = rejection
    ? `${number.format(rejection.findings.length)} ${
        rejection.findings.length === 1 ? 'error' : 'errores'
      }${rows ? ` en ${number.format(rows)} ${rows === 1 ? 'fila' : 'filas'}` : ''}`
    : '';
  const rejectionTitle = rejection?.findings.every(
    (finding) => finding.row === null
  )
    ? rejection.findings.some((finding) => finding.code === 'INVALID_FILE')
      ? 'No pudimos leer el archivo'
      : rejection.findings.some((finding) => finding.code === 'EMPTY_FILE')
        ? 'El archivo está vacío'
        : 'Revisa los datos de la carga'
    : 'Revisa los errores del archivo';
  const announcement =
    state.kind === 'processing'
      ? 'Procesando el archivo. Espera a que termine la carga.'
      : state.kind === 'rejected'
        ? `${errorCount}. No se guardó ningún registro de esta carga.`
        : state.kind === 'success'
          ? `${IMPORT_SUCCESS_MESSAGE} ${
              state.summary
                ? metrics
                    .map(
                      ([key, label]) =>
                        `${label}: ${number.format(state.summary![key])}`
                    )
                    .join('. ')
                : 'La carga terminó, pero el resumen no está disponible.'
            }`
          : state.kind === 'uncertain'
            ? 'No pudimos confirmar cómo terminó esta carga. Revisa los contratos antes de volver a enviarla.'
            : state.kind === 'upload-error'
              ? 'No pudimos subir el archivo. La importación no se inició.'
              : '';

  useEffect(() => {
    if (state.kind !== 'editing' || showPrevious) heading.current?.focus();
  }, [state.kind, showPrevious]);

  return (
    <div className={styles.root} id="upload-contract-container">
      <Button
        className={styles.back}
        disabled={flow.isBusy}
        onClick={() => navigate(-1)}
        icon={<ArrowLeftOutlined />}
      >
        Regresar
      </Button>
      <h1>Subir contratos por excel</h1>
      <div
        className={styles.srOnly}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {announcement}
      </div>
      <input
        ref={flow.picker}
        type="file"
        accept=".xlsx,.xls,.csv"
        hidden
        disabled={flow.isBusy}
        aria-label="Seleccionar archivo de contratos"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = '';
          if (file) flow.selectFile(file);
        }}
      />
      {flow.selectionError && (
        <Alert
          type="error"
          showIcon
          title={flow.selectionError}
          className={styles.selectionError}
        />
      )}
      {/* Keep the form mounted so a correction preserves all option values. */}
      <div hidden={state.kind !== 'editing' || showPrevious}>
        <ImportForm
          flow={flow}
          onShowPrevious={() => flow.setShowPrevious(true)}
        />
      </div>
      {rejection && (
        <>
          <div className={styles.resultHeader}>
            <h2 tabIndex={-1} ref={heading}>
              <ExclamationCircleOutlined aria-hidden="true" />{' '}
              {showPrevious
                ? 'Resultado anterior · antes de esta selección'
                : rejectionTitle}
            </h2>
            {showPrevious ? (
              <Button
                onClick={() => {
                  flow.setShowPrevious(false);
                  requestAnimationFrame(() =>
                    document.getElementById('previous-import-errors')?.focus()
                  );
                }}
              >
                Volver al archivo seleccionado
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={flow.openPicker}
                data-cy="contract-import-correct"
              >
                Seleccionar archivo corregido
              </Button>
            )}
          </div>
          <div className={styles.rejection} data-cy="contract-import-rejected">
            <strong>
              {showPrevious
                ? 'La carga anterior no guardó ningún registro. Estos errores corresponden al archivo anterior.'
                : IMPORT_REJECTION_MESSAGE}
            </strong>
            <p>{errorCount}</p>
          </div>
          <ImportContextCard context={rejection.context} />
          <ImportFindings
            key={showPrevious ? 'previous' : 'current'}
            findings={rejection.findings}
          />
          <ImportHelp />
        </>
      )}
      {state.kind === 'processing' && (
        <section
          className={`${styles.card} ${styles.result}`}
          aria-busy="true"
          data-cy="contract-import-processing"
        >
          <Spin size="large" />
          <h2 tabIndex={-1} ref={heading}>
            Procesando el archivo
          </h2>
          <p>
            Estamos subiendo y revisando el archivo antes de guardar. Esto puede
            demorar algunos minutos.
          </p>
          <p>
            No cierres esta pestaña ni vuelvas a enviar el archivo mientras
            termina.
          </p>
          <ImportContextCard context={state.context} />
        </section>
      )}
      {state.kind === 'success' && (
        <section
          className={`${styles.card} ${styles.result}`}
          data-cy="contract-import-success"
        >
          <CheckCircleOutlined
            className={styles.successIcon}
            aria-hidden="true"
          />
          <h2 tabIndex={-1} ref={heading}>
            {IMPORT_SUCCESS_MESSAGE}
          </h2>
          <p>
            La carga terminó. Puedes consultar los contratos o subir otro
            archivo.
          </p>
          <ImportContextCard context={state.context} />
          <h3>Resumen de esta carga</h3>
          {state.summary ? (
            <>
              <dl className={styles.metrics}>
                {metrics.map(([key, label]) => (
                  <div key={key}>
                    <dt>{label}</dt>
                    <dd data-cy={`contract-import-${key}`}>
                      {number.format(state.summary![key])}
                    </dd>
                  </div>
                ))}
              </dl>
              <p>Los contratos existentes recibieron nuevos horarios.</p>
            </>
          ) : (
            <p data-cy="contract-import-summary-unavailable">
              La carga terminó, pero el resumen no está disponible.
            </p>
          )}
          <div className={styles.actions}>
            <Button
              type="primary"
              onClick={() =>
                navigate(
                  `${incidencePaths.basePath}/${incidencePaths.reconcileBulk}`,
                  {
                    state: {
                      contractKindId: state.context.contractKindId,
                      applicationDate: state.context.applicationDate,
                    },
                  }
                )
              }
              data-cy="contract-import-reconcile-incidences"
            >
              Conciliar incidencias
            </Button>
            <Button
              onClick={() => navigate(usersPaths.basePath)}
            >
              Volver a empleados
            </Button>
            <Button onClick={flow.openPicker}>Subir otro archivo</Button>
          </div>
        </section>
      )}
      {(state.kind === 'upload-error' ||
        state.kind === 'uncertain' ||
        state.kind === 'access-error') && (
        <section
          className={`${styles.card} ${styles.result}`}
          data-cy={`contract-import-${state.kind}`}
        >
          <ExclamationCircleOutlined
            className={styles.warningIcon}
            aria-hidden="true"
          />
          <h2 tabIndex={-1} ref={heading}>
            {state.kind === 'upload-error'
              ? 'No pudimos subir el archivo'
              : state.kind === 'access-error'
                ? 'No tienes acceso a esta carga'
                : 'No pudimos confirmar cómo terminó esta carga'}
          </h2>
          <p>
            {state.kind === 'upload-error'
              ? 'La importación no se inició. Puedes volver a subir el archivo.'
              : state.kind === 'access-error'
                ? 'Revisa tu sesión y tus permisos antes de continuar.'
                : 'No sabemos si se guardaron todos, algunos o ninguno de los registros. Revisa los contratos de los empleados involucrados antes de volver a enviar el archivo.'}
          </p>
          <ImportContextCard context={state.context} />
          {state.kind === 'upload-error' ? (
            <Button type="primary" onClick={flow.retryUpload}>
              Volver a subir archivo
            </Button>
          ) : (
            <a
              className={styles.templateLink}
              href={usersPaths.basePath}
              target="_blank"
              rel="noopener noreferrer"
            >
              Revisar contratos en empleados (nueva pestaña)
            </a>
          )}
        </section>
      )}
    </div>
  );
};
